import React, {useEffect, useRef, useState, forwardRef} from "react";
import {t} from "../../../shared-services/translate";
import {FaMapMarkerAlt, FaAngleDown} from "react-icons/fa";
import RoundCTA from "../../shared/RoundCTA";
import {FarmyColumn, FarmyColumnSet} from "../../layouts/components/FarmyColumns";

import {Link} from "react-router-dom";
import useWindowTools from "../../../react-hooks/useWindowTools";

const ProducerBlockPDP = forwardRef(function ProducerBlock(props, ref) {
  const defaultLineClamp = 5;
  const {producer = {}, scrollToInfoBlock} = props;
  const {isMobile} = useWindowTools();
  const producerNameRef = useRef();
  const [lineClamp, setLineClamp] = useState(defaultLineClamp);

  // Hack-ish alert! We should find a better way to accomplish this through layout/CSS only, for now too tricky.
  //
  // It returns the number of lines that the producer description should display
  // so that "Information" label falls roughly at the same height as the product name, under "details".
  // Desktop only => else we pass directly the maximum num of lines.
  const getLineClamp = () => {
    const lineClampValues = {
      60: 4,
      90: 3,
      100: 2
    };

    const mobileClampValue = 3;
    if (isMobile) return mobileClampValue;

    const minClamp = Math.min(...Object.values(lineClampValues));

    const titleElement = document.querySelector("h1.product-title");
    const producerNameElement = producerNameRef.current;

    const topHeight = titleElement?.clientHeight + producerNameElement?.clientHeight;

    const topHeightTier = Object
      .keys(lineClampValues)
      .filter(key => topHeight < key)
      .sort((a, b) => a < b)[0];

    return lineClampValues[topHeightTier] || minClamp;
  };

  useEffect(() => {
    requestAnimationFrame(() => {
      setLineClamp(getLineClamp());
    });
  }, [producer]);
  return <div>
    <FarmyColumnSet className={`
      ${isMobile ? "flex" : null}
      gap-10
      align-items-flex-start
    `}>
      <FarmyColumn width="100%">
        <div className="producer-name" ref={producerNameRef}>
          {t("product_page.producer")}

          <span
            className="margin-left-5 text-strong text-decoration-underline">
            <Link to={producer?.seo_url || "#"} className="text-color-green">
              {producer?.name}
            </Link>
          </span>
        </div>

        {isMobile && <RegionBlock producer={producer} isMobile={true}/>}
      </FarmyColumn>

      {isMobile &&
        <FarmyColumn width="60px" strict={true}>
          <RoundCTA
            onClick={scrollToInfoBlock}
            className="text-color-white soft-shadow"
            style={{
              backgroundColor: "rgba(17, 116, 50, 0.42)"
            }}
          >
            <div className="margin-top-5" style={{lineHeight: "1em"}}>info</div>
            <FaAngleDown/>
          </RoundCTA>
        </FarmyColumn>
      }
    </FarmyColumnSet>

    {!isMobile && <>
      <ImageBlock producer={producer} className="margin-top-10" isMobile={isMobile}/>
      <TextBlock producer={producer} isMobile={isMobile} lineClamp={lineClamp}/>
    </>}

    {isMobile && <>
      <TextBlock producer={producer} isMobile={isMobile} lineClamp={lineClamp}/>
      <ImageBlock producer={producer} isMobile={isMobile} ref={ref}/>
    </>}
  </div>;
});

export default ProducerBlockPDP;

const ImageBlock = forwardRef(function ImageBlock(props, ref) {
  const {producer, className = "", isMobile} = props;
  const safeTimeout = 500;

  const [mobileHeaderHeight, setHeaderHeight] = useState(null);

  useEffect(() => {
    setTimeout(() => {
      requestAnimationFrame(() => {
        const headerMobileElement = document.querySelector(".header-mobile");
        const height = headerMobileElement?.clientHeight;
        setHeaderHeight(height);
      });
    }, safeTimeout);
  }, [producer]);

  return <div
    className={`
      producer-image
      position-relative
      overflow-hidden
      border-radius-xl
      ${className}
      `}
  >
    <div
      className="position-absolute full-width full-height"
      ref={ref}
      style={{
        bottom: 0,
        left: 0,
        zIndex: -1,
        height: mobileHeaderHeight ? `${mobileHeaderHeight}px` : null
      }}
    >
    </div>

    <Link
      to={producer?.seo_url || "#"}
      className={"background-image-cover full-width"}
      style={{
        display: "block",
        aspectRatio: isMobile ? "1.4" : "1.1",
        backgroundImage: `url('${producer?.avatar?.url}')`
      }}
    />
  </div>;
});

const TextBlock = (props) => {
  const {producer, isMobile, className = "", lineClamp} = props;

  const description = producer?.short_description;

  return <>
    {!isMobile && <RegionBlock producer={producer}/>}

    <div className="position-relative">
      <p
        className={`
            display-inline-block
            producer-description
            text-small
            margin-top-10
            line-clamped
            clamped-${lineClamp}-lines
            ${className}
          `}
        style={{
          textOverflow: "ellipsis",
          color: "initial"
        }}
      >
        {description}
      </p>

      <Link to={producer?.seo_url || "#"}>
        <div
          className="position-absolute right-0 bottom-0 text-small text-color-light-blue capitalize"
          style={{
            paddingLeft: "45px",
            background: "linear-gradient(to right, transparent, white, 90%, white)"
          }}
        >
          ... {t("more")}
        </div>
      </Link>
    </div>
  </>;
};

const RegionBlock = (props) => {
  const {producer, isMobile, className = ""} = props;
  const regions = producer?.sales_regions || [];
  const region = regions[0];

  return region
    ? <div className={`
      producer-location
      flex gap-5
      align-items-flex-start
      ${isMobile ? "margin-top-10" : "margin-top-20"}
      ${className}`
    }>
      <FaMapMarkerAlt
        className="text-smaller"
        fill='transparent'
        stroke='grey'
        strokeWidth='35px'
        style={{overflow: "visible"}}
      />
      <span className="text-smaller font-weight-600">
        {region?.name}
      </span>

      <img
        alt={region?.name}
        className="display-inline"
        src={`/flags/v2/50/${region?.flag}.png`}
        style={{width: "15px"}}
      />
    </div>
    : <></>;
};
